import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const LIST_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/clarityFormsList';

const STATUS_TEXT = {
  webflow_site: 'Webflow site: forms can’t be pulled automatically. Keep uploading them to the Forms Clarity slot.',
  no_wp_key: 'No Core6 Publisher key on this client, so the hub can’t read the site’s forms.',
  plugin_outdated: 'The site needs Core6 Ultimate 1.0.20 or newer before its forms can be listed.',
  no_gravity_forms: 'Gravity Forms isn’t active on this site.',
};

// "Forms in Clarity report" checklist. Opt-in: only ticked Gravity Forms are
// pulled into the monthly report; nothing is pulled until forms are picked.
// The list is read live from the site on every load (and Refresh), so a form
// added to the site later shows up here marked NEW. Saving records the IDs
// shown (clarityFormsSeen) so the next new form stands out.
export default function ClarityFormsPicker({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSavedMsg(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(LIST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
      setPicked(json.selectedIds || []);
    } catch (e) {
      setError('Could not load this site’s forms. Try Refresh.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const forms = data?.forms || [];
  const saved = data?.selectedIds || [];
  const isJobTread = data?.source === 'jobtread';
  const dirty = picked.length !== saved.length || picked.some(id => !saved.includes(id))
    || (forms.some(f => f.isNew) && !loading);
  const toggle = id => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const save = async () => {
    setSaving(true);
    setSavedMsg(null);
    try {
      // Keep any ticked ID the site no longer lists out of the saved selection.
      // JobTread clients keep their own selection (string form IDs) so it can
      // never be confused with a Gravity Forms one.
      const valid = picked.filter(id => forms.some(f => f.id === id));
      await updateDoc(doc(db, 'clients', clientId), isJobTread ? {
        clarityJobtreadFormIds: valid,
        clarityJobtreadFormsSeen: forms.map(f => f.id),
        clarityFormsUpdatedAt: serverTimestamp(),
      } : {
        clarityFormIds: valid,
        clarityFormsSeen: forms.map(f => f.id),
        clarityFormsUpdatedAt: serverTimestamp(),
      });
      setData(d => ({
        ...d,
        selectedIds: valid,
        configured: true,
        missingSelected: [],
        forms: d.forms.map(f => ({ ...f, selected: valid.includes(f.id), isNew: false })),
      }));
      setPicked(valid);
      setSavedMsg(valid.length ? `Saved. ${valid.length} form${valid.length === 1 ? '' : 's'} will be in the next report.` : 'Saved. No forms will be in the report.');
    } catch (e) {
      setSavedMsg('Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const label = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' };
  const note = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 };

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={label}>Forms in Clarity Report</div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading || saving}>
          {loading ? 'Loading…' : 'Refresh forms'}
        </button>
      </div>

      {error && <div style={{ ...note, color: '#f87171' }}>{error}</div>}

      {!loading && data && data.status !== 'ok' && (
        <div style={note}>
          {STATUS_TEXT[data.status]
            || (String(data.status).startsWith('jobtread_error')
              ? `Couldn’t read JobTread with this client’s grant key. Check the key in Content Settings (keys expire after 3 months unused). (${data.status})`
              : `Couldn’t read the site’s forms (${data.status}).`)}
        </div>
      )}
      {!loading && isJobTread && data.status === 'ok' && (
        <div style={note}>Leads come from JobTread web forms. Only the request, city and form answers are pulled; never contact details.</div>
      )}

      {!loading && data?.status === 'ok' && (
        <>
          {!data.configured && (
            <div style={{ ...note, color: '#f59e0b' }}>
              ⚠ No forms picked yet. Forms stay out of this client’s report until you tick them and save.
            </div>
          )}
          {data.supportsFilter === false && (
            <div style={{ ...note, color: '#f59e0b' }}>
              ⚠ Site is on Core6 Ultimate {data.ultimateVersion}. Update it to 1.0.20 so unticked forms never leave the site.
            </div>
          )}
          {(data.missingSelected || []).length > 0 && (
            <div style={{ ...note, color: '#f59e0b' }}>
              ⚠ {data.missingSelected.length} ticked form{data.missingSelected.length === 1 ? ' is' : 's are'} no longer on the site (ID {data.missingSelected.join(', ')}). Save to clear.
            </div>
          )}
          {forms.some(f => f.isNew) && (
            <div style={{ ...note, color: 'var(--text-primary)' }}>
              <strong>NEW</strong> forms appeared on the site since this list was last saved. They are not included until ticked.
            </div>
          )}

          {forms.length === 0 ? (
            <div style={note}>This site has no Gravity Forms.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
              {forms.map(f => (
                <label
                  key={f.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                    background: picked.includes(f.id) ? 'var(--bg-primary)' : 'transparent',
                    border: '1px solid', borderColor: picked.includes(f.id) ? 'var(--border)' : 'transparent',
                  }}
                >
                  <input type="checkbox" checked={picked.includes(f.id)} onChange={() => toggle(f.id)} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-primary)' }}>
                    {f.title}
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{isJobTread ? ' · JobTread' : ` · #${f.id}`}{f.isActive ? '' : ' · inactive'}</span>
                  </span>
                  {f.isNew && (
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', padding: '2px 6px', borderRadius: 4, background: 'var(--text-primary)', color: 'var(--bg-primary)' }}>NEW</span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {f.entries30d == null ? '' : `${f.entries30d} in last 30 days`}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save form selection'}
            </button>
            {savedMsg && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{savedMsg}</span>}
          </div>
        </>
      )}
    </div>
  );
}
